import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AGENT_CAPABILITIES, type JobDTO, type ShellSessionDTO, type VpsDTO } from '@devrobot/shared';
import { KeySquare, TerminalSquare, XCircle } from 'lucide-react';
import { api, seg } from '../../api/client';
import { useApiMutation, useShellSessions } from '../../api/queries';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { CopyButton } from '../../components/CopyButton';
import { ErrorAlert } from '../../components/ErrorAlert';
import { Section } from '../../components/PageHeader';
import { RelativeTime } from '../../components/RelativeTime';
import { durationBetween, formatBytes, formatDuration } from '../../lib/format';

/** Why the agent cannot provide a feature right now (null when it can). */
function unavailableReason(vps: VpsDTO, capability: string, missing: string): string | null {
  if (vps.connection !== 'connected') return 'The agent is not connected';
  if (!vps.agent.capabilities.includes(capability)) return missing;
  return null;
}

export const shellUnavailableReason = (vps: VpsDTO) => unavailableReason(vps, AGENT_CAPABILITIES.tunnel, 'Upgrade the agent, or shell access is disabled in the agent configuration');

/** Header action: opens the browser terminal (render for platform admins only). */
export function ConnectSshButton({ vps }: { vps: VpsDTO }) {
  const navigate = useNavigate();
  const reason = shellUnavailableReason(vps);
  const button = (
    <button type="button" className="btn btn-sm" disabled={Boolean(reason)} onClick={() => navigate(`/vps/${seg(vps.id)}/terminal`)}>
      <TerminalSquare className="size-4" aria-hidden="true" /> Connect SSH
    </button>
  );
  if (!reason) return button;
  return (
    <span className="tooltip tooltip-bottom" data-tip={reason} tabIndex={0} aria-label={`Connect SSH unavailable: ${reason}`}>
      {button}
    </span>
  );
}

function SourceBadge({ s }: { s: ShellSessionDTO }) {
  return (
    <span className="flex flex-wrap gap-1">
      <span className="badge badge-ghost badge-xs">{s.source}</span>
      <span className="badge badge-ghost badge-xs">{s.kind === 'tcp' ? 'native ssh' : s.command ? 'command' : 'shell'}</span>
    </span>
  );
}

/** Active and recent shell sessions (platform admins only). */
export function ShellSessionsCard({ vps }: { vps: VpsDTO }) {
  const active = useShellSessions(vps.id, { active: true, limit: 50 });
  const all = useShellSessions(vps.id, { active: false, limit: 30 });
  const [terminating, setTerminating] = useState<ShellSessionDTO | null>(null);
  const terminate = useApiMutation((sid: string) => api.delete<ShellSessionDTO>(`/api/v1/shell-sessions/${seg(sid)}`), { invalidate: [['vps', vps.id]] });
  const recent = (all.data ?? []).filter((s) => !s.active).slice(0, 10);

  return (
    <Section title="Shell sessions" description="Interactive root shells and native SSH tunnels through DevRobot. Every session is audited.">
      <h3 className="text-xs font-semibold tracking-wide text-base-content/55 uppercase">Active</h3>
      {active.isError ? (
        <ErrorAlert error={active.error} onRetry={() => void active.refetch()} />
      ) : active.isPending ? (
        <div className="skeleton h-8 w-full rounded-field" />
      ) : active.data.length === 0 ? (
        <p className="text-sm text-base-content/60">No active sessions.</p>
      ) : (
        <>
          <ul className="divide-y divide-hairline md:hidden">
            {active.data.map((s) => (
              <li key={s.id} className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-x-2">
                    <span className="inline-grid *:[grid-area:1/1]" aria-hidden="true">
                      <span className="status status-success status-sm animate-ping" />
                      <span className="status status-success status-sm" />
                    </span>
                    <span className="truncate font-medium">{s.user.name}</span>
                    <span className="text-xs text-base-content/60">@{s.user.username}</span>
                  </div>
                  <SourceBadge s={s} />
                  <div className="flex flex-wrap gap-x-3 text-xs text-base-content/60">
                    <RelativeTime value={s.startedAt ?? s.createdAt} />
                    <span className="font-mono">
                      {formatBytes(s.bytesIn)} / {formatBytes(s.bytesOut)}
                    </span>
                  </div>
                </div>
                <button type="button" className="btn btn-soft btn-error btn-xs shrink-0" onClick={() => setTerminating(s)}>
                  <XCircle className="size-3" aria-hidden="true" /> Terminate
                </button>
              </li>
            ))}
          </ul>
          <div className="overflow-x-auto max-md:hidden">
            <table className="table table-sm">
              <thead>
                <tr className="text-xs tracking-wide text-base-content/50 uppercase">
                  <th>User</th>
                  <th>Source</th>
                  <th>Started</th>
                  <th>In / out</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {active.data.map((s) => (
                  <tr key={s.id} className="hover:bg-base-200/50">
                    <td>
                      <div className="font-medium">{s.user.name}</div>
                      <div className="text-xs text-base-content/60">@{s.user.username}</div>
                    </td>
                    <td>
                      <SourceBadge s={s} />
                    </td>
                    <td className="text-xs">
                      <RelativeTime value={s.startedAt ?? s.createdAt} />
                    </td>
                    <td className="whitespace-nowrap font-mono text-xs">
                      {formatBytes(s.bytesIn)} / {formatBytes(s.bytesOut)}
                    </td>
                    <td className="text-right">
                      <button type="button" className="btn btn-soft btn-error btn-xs" onClick={() => setTerminating(s)}>
                        <XCircle className="size-3" aria-hidden="true" /> Terminate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h3 className="text-xs font-semibold tracking-wide text-base-content/55 uppercase">Recent</h3>
      {all.isError ? (
        <ErrorAlert error={all.error} onRetry={() => void all.refetch()} />
      ) : all.isPending ? (
        <div className="skeleton h-8 w-full rounded-field" />
      ) : recent.length === 0 ? (
        <p className="text-sm text-base-content/60">No ended sessions yet.</p>
      ) : (
        <ul className="divide-y divide-hairline text-sm">
          {recent.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-1.5">
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate font-medium">{s.user.name}</span>
                <SourceBadge s={s} />
              </span>
              <span className="flex flex-wrap items-center gap-x-3 text-xs text-base-content/60">
                <span>{s.endReason ?? 'ended'}</span>
                {s.exitCode !== null ? <span>exit {s.exitCode}</span> : null}
                <span>{formatDuration(durationBetween(s.startedAt ?? s.createdAt, s.endedAt))}</span>
                <RelativeTime value={s.endedAt} />
              </span>
            </li>
          ))}
        </ul>
      )}

      {terminating ? (
        <ConfirmDialog
          title={`Terminate ${terminating.user.name}'s session?`}
          confirmLabel="Terminate session"
          tone="error"
          busy={terminate.isPending}
          error={terminate.error}
          onCancel={() => (setTerminating(null), terminate.reset())}
          onConfirm={() => terminate.mutate(terminating.id, { onSuccess: () => setTerminating(null) })}
        >
          <p className="text-sm text-base-content/70">The connection is closed immediately and any process running in that shell is ended. The termination is recorded in the audit log.</p>
        </ConfirmDialog>
      ) : null}
    </Section>
  );
}

/** Native SSH (DevRobot SSH user CA) status and toggle (platform admins only). */
export function NativeSshSection({ vps }: { vps: VpsDTO }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const toggle = useApiMutation((enabled: boolean) => api.post<{ job: JobDTO }>(`/api/v1/vps/${seg(vps.id)}/native-ssh`, { enabled }), { invalidate: [['vps', vps.id], ['jobs']] });
  const enabled = vps.nativeSsh.enabled;
  const reason = unavailableReason(vps, AGENT_CAPABILITIES.sshCa, 'Upgrade the agent to manage native SSH');
  const host = `devrobot-${vps.name}`;

  const button = (
    <button type="button" className={`btn btn-sm ${enabled ? 'btn-ghost dr-ink-warning' : 'btn-primary'}`} disabled={Boolean(reason) || toggle.isPending} onClick={() => setConfirming(true)}>
      <KeySquare className="size-4" aria-hidden="true" /> {enabled ? 'Disable' : 'Enable'}
    </button>
  );

  return (
    <Section
      title="Native SSH"
      description="Lets your own SSH client connect through DevRobot with short-lived certificates."
      actions={
        reason ? (
          <span className="tooltip tooltip-left" data-tip={reason} tabIndex={0} aria-label={`Native SSH change unavailable: ${reason}`}>
            {button}
          </span>
        ) : (
          button
        )
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {enabled ? <span className="badge badge-success badge-soft badge-sm">enabled</span> : <span className="badge badge-ghost badge-sm">disabled</span>}
        {vps.nativeSsh.hostKeys > 0 ? <span className="text-base-content/60">{vps.nativeSsh.hostKeys} host key{vps.nativeSsh.hostKeys === 1 ? '' : 's'} pinned</span> : null}
      </div>
      <ErrorAlert error={toggle.error} />
      <div className="space-y-2 text-sm text-base-content/70">
        {enabled ? <p>Configure your SSH client once, then connect:</p> : <p>After enabling, configure your SSH client once, then connect:</p>}
        <CommandLine cmd="devrobot ssh-config --write" />
        <CommandLine cmd={`ssh ${host}`} />
        <p>
          Or directly: <code className="font-mono text-xs">devrobot ssh {vps.name} --native</code>. For a plain shell without native SSH: <code className="font-mono text-xs">devrobot ssh {vps.name}</code>.
        </p>
      </div>

      {confirming ? (
        <ConfirmDialog
          title={enabled ? `Disable native SSH on ${vps.name}?` : `Enable native SSH on ${vps.name}?`}
          confirmLabel={enabled ? 'Disable native SSH' : 'Enable native SSH'}
          tone={enabled ? 'warning' : 'primary'}
          busy={toggle.isPending}
          error={toggle.error}
          onCancel={() => (setConfirming(false), toggle.reset())}
          onConfirm={() => toggle.mutate(!enabled, { onSuccess: (r) => navigate(`/jobs/${r.job.id}`) })}
        >
          <p className="text-sm text-base-content/70">
            {enabled
              ? 'The agent removes the DevRobot SSH certificate authority from the server’s sshd configuration. Existing DevRobot certificates stop working; other SSH access is not changed.'
              : 'The agent installs the DevRobot SSH certificate authority in the server’s sshd configuration so platform administrators can connect with short-lived certificates. Existing SSH access is not changed.'}
          </p>
        </ConfirmDialog>
      ) : null}
    </Section>
  );
}

function CommandLine({ cmd }: { cmd: string }) {
  return (
    <div className="flex items-center gap-1 rounded-field bg-base-200/60 py-1 pr-1 pl-3">
      <code className="min-w-0 flex-1 break-anywhere font-mono text-xs">$ {cmd}</code>
      <CopyButton value={cmd} label={`Copy ${cmd}`} />
    </div>
  );
}
